import SwivuModal from "./SwivuModal.jsx";
import { useCallback } from "react";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { EDITOR_CONTEXT_MENU_ITEM_KEYS, TASK_BUDGET_FIELDS } from "../../../other/constants/OtherKeys.jsx";
import { annotateField, DEFAULT_BUDGET_FIELDS } from "../../../other/utils/budgetUtils.jsx";
import { checkIfBudgetIdExists } from "../../../network/network.jsx";
import { defaultCellRenderer, lineThroughString, showArchiveRecordAlert, showToast } from "../../../other/utils/utils.jsx";
import { DateHelper } from "@bryntum/schedulerpro";

class SwivuBudgetEditor extends EditorBase {
  static get $name() {
    return "SwivuBudgetEditor";
  }

  static get type() {
    return WIDGET_KEYS.swivuBudgetEditor;
  }

  static get configurable() {
    return {
      ...super.configurable,
      title: "New Budget",
      minWidth: "73em",
      maxWidth: "100vw",
      width: "70em",
      stateId: PERSISTENCE_STATE_KEYS.budgetEditor,
      minHeight: "600px",
      maxHeight: "100vh",
      height: "min(100vh, 650px)",
      closable: true,
      autoClose: false,
      draggable: true,
      modal: true,
      scrollable: true,
      autoUpdateFields: true,
      cls: "resize minimal-gradient-editor swivu-header-button-light",
      listeners: {
        hide: "onEditorHide"
      },
      items: {
        note: {
          type: "text",
          cls: "w-full !flex-[1_0_100%] !@md:flex-[1_0_100%] py-2 border-0 border-l-4 border-solid border-[#e0e0e0] dark:border-[#333] pl-[6px] bg-[#fafafa] dark:bg-[#252525] dark:text-[#a5a5a5]",
          content: "Budgets can be independent, exported, imported, and linked to any number of tasks."
        },
        headerContainer: {
          type: "container",
          cls: "w-full gap-x-[0.9em]",
          defaults: {
            cls: "!flex-[1_0_49%]",
            labelWidth: "11em",
            onChange: "up.onRecordChange",
          },
          items: {
            budgetIdField: {
              name: TASK_BUDGET_FIELDS.title,
              type: "text",
              label: "Budget ID",
              cls: "!flex-[1_0_100%]",
              onInput: "up.onBudgetIdFieldInput",
              triggers: false
            },
            linkedTaskField: {
              type: "combo",
              label: "Linked Task",
              name: "budgetTasks",
              displayField: "name",
              multiSelect : true,
              cls: "!flex-[1_0_100%]",
              height: 100,
              onChange: "up.onLinkedTaskChange",
              listItemTpl : (record) => lineThroughString(record.name, record.archived),
              chipView: {
                cls: "!items-start !m-[0.5em]"
              }
            },
            descriptionField: {
              name: TASK_BUDGET_FIELDS.description,
              label: "Description",
              type: "text",
              triggers: false
            },
            contractAmountField: {
              name: TASK_BUDGET_FIELDS.contractAmount,
              label: "Contract Amount",
              type: "number",
              min: 0,
              ...DEFAULT_BUDGET_FIELDS.amountField,
              triggers: false
            },
            changeOrdersField: {
              name: TASK_BUDGET_FIELDS.changeOrders,
              label: "Change Orders",
              type: "number",
              min: 0,
              ...DEFAULT_BUDGET_FIELDS.amountField,
              triggers: false
            },
            totalContractField: {
              name: TASK_BUDGET_FIELDS.totalContract,
              label: "Total Contract",
              type: "number",
              disabled: true,
              readOnly: true,
              ...DEFAULT_BUDGET_FIELDS.amountField,
              triggers: false
            },
          }
        },
        bodyContainer: {
          type: "container",
          cls: "w-full pt-5 mt-5 border-[0] border-t-[0.5px] border-solid border-[#FFFFFF] dark:border-[#000000] gap-x-[0.9em]",
          defaults: {
            cls: "!flex-[1_0_32%] min-w-[250px]",
          },
          items: {
            bodyEstimationFieldContainer: {
              type: "container",
              defaults: {
                labelWidth: "11em",
                onChange: "up.onRecordChange",
                triggers: false
              },
              items: {
                laborEstimatedField: {
                  name: TASK_BUDGET_FIELDS.laborEstimated,
                  label: "Labor Estimated",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                materialEstimatedField: {
                  name: TASK_BUDGET_FIELDS.materialsEstimated,
                  label: "Materials Estimated",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                subsEstimatedField: {
                  name: TASK_BUDGET_FIELDS.subsEstimated,
                  label: "Subs Estimated",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                rentalEstimatedField: {
                  name: TASK_BUDGET_FIELDS.rentalsEstimated,
                  label: "Rentals Estimated",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                miscellaneousEstimatedField: {
                  name: TASK_BUDGET_FIELDS.miscellaneousEstimated,
                  label: "Miscellaneous Estimated",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                daysEstimatedField: {
                  name: TASK_BUDGET_FIELDS.daysEstimated,
                  label: "Days Estimated",
                  type: "number",
                  min: 0
                },
              }
            },
            bodyActualFieldContainer: {
              type: "container",
              defaults: {
                labelWidth: "11em",
                onChange: "up.onRecordChange",
                triggers: false
              },
              items: {
                laborActualField: {
                  name: TASK_BUDGET_FIELDS.laborActual,
                  label: "Labor Actual",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                materialActualField: {
                  name: TASK_BUDGET_FIELDS.materialsActual,
                  label: "Materials Actual",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                subsActualField: {
                  name: TASK_BUDGET_FIELDS.subsActual,
                  label: "Subs Actual",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                rentalActualField: {
                  name: TASK_BUDGET_FIELDS.rentalsActual,
                  label: "Rentals Actual",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                miscellaneousActualField: {
                  name: TASK_BUDGET_FIELDS.miscellaneousActual,
                  label: "Miscellaneous Actual",
                  type: "number",
                  min: 0,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                daysActualField: {
                  name: TASK_BUDGET_FIELDS.daysActual,
                  label: "Days Actual",
                  type: "number",
                  disabled: true,
                  readOnly: true,
                },
              }
            },
            bodyVarianceFieldContainer: {
              type: "container",
              defaults: {
                labelWidth: "11em",
                onChange: "up.onRecordChange",
                triggers: false
              },
              items: {
                laborVarianceField: {
                  name: TASK_BUDGET_FIELDS.laborVariance,
                  label: "Labor Variance",
                  type: "number",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                materialVarianceField: {
                  name: TASK_BUDGET_FIELDS.materialsVariance,
                  label: "Materials Variance",
                  type: "number",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                subsVarianceField: {
                  name: TASK_BUDGET_FIELDS.subsVariance,
                  label: "Subs Variance",
                  type: "number",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                rentalVarianceField: {
                  name: TASK_BUDGET_FIELDS.rentalsVariance,
                  label: "Rentals Variance",
                  type: "number",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                miscellaneousVarianceField: {
                  name: TASK_BUDGET_FIELDS.miscellaneousVariance,
                  label: "Miscellaneous Variance",
                  type: "number",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                daysVarianceField: {
                  name: TASK_BUDGET_FIELDS.daysVariance,
                  label: "Days Variance",
                  type: "number",
                  disabled: true,
                  readOnly: true,
                },
              }
            },
          }
        },
        footerContainer: {
          type: "container",
          cls: "w-full pt-5 mt-5 border-[0] border-t-[0.5px] border-solid border-[#FFFFFF] dark:border-[#000000] gap-x-[0.9em]",
          defaults: {
            cls: "!flex-[1_0_32%] min-w-[250px]"
          },
          items: {
            footerEstimatedFieldContainer: {
              type: "container",
              defaults: {
                 labelWidth: "11em",
                 onChange: "up.onRecordChange",
                 triggers: false
              },
              items: {
                totalCostEstimatedField: {
                  name: TASK_BUDGET_FIELDS.totalCostEstimated,
                  type: "text",
                  label: "Total Costs Estimated",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                grossProfitEstimatedField: {
                  name: TASK_BUDGET_FIELDS.grossProfitEstimated,
                  type: "text",
                  label: "Gross Profit Estimated",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                grossMarginEstimatedField: {
                  name: TASK_BUDGET_FIELDS.grossMarginEstimated,
                  type: "text",
                  label: "Gross Margin Estimated",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.percentageField
                },
              }
            },
            footerActualFieldContainer: {
              type: "container",
              defaults: {
                 labelWidth: "11em",
                 onChange: "up.onRecordChange",
                 triggers: false
              },
              items: {
                totalCostActualField: {
                  name: TASK_BUDGET_FIELDS.totalCostActual,
                  type: "text",
                  label: "Total Cost Actual",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                grossProfitActualField: {
                  name: TASK_BUDGET_FIELDS.grossProfitActual,
                  type: "text",
                  label: "Gross Profit Actual",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                grossMarginActualField: {
                  name: TASK_BUDGET_FIELDS.grossMarginActual,
                  type: "text",
                  label: "Gross Margin Actual",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.percentageField
                },
              }
            },
            footerVarianceFieldContainer: {
              type: "container",
              defaults: {
                 labelWidth: "11em",
                 onChange: "up.onRecordChange",
                 triggers: false
              },
              items: {
                totalCostVarianceField: {
                  name: TASK_BUDGET_FIELDS.totalCostVariance,
                  type: "text",
                  label: "Total Cost Variance",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                grossProfitVarianceField: {
                  name: TASK_BUDGET_FIELDS.grossProfitVariance,
                  type: "text",
                  label: "Gross Profit Variance",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.amountField
                },
                grossMarginVarianceField: {
                  name: TASK_BUDGET_FIELDS.grossMarginVariance,
                  type: "text",
                  label: "Gross Margin Variance",
                  disabled: true,
                  readOnly: true,
                  ...DEFAULT_BUDGET_FIELDS.percentageField
                }
              }
            }
          }
        }
      },
      bbar: {
        cls: "dark:!bg-[#2c2a2e]",
        items: {
          dateMetaDataField: {
            type: "label",
            hidden: false,
            cls: 'mr-auto pl-[1em]'
          },
          closeButton: {
            type: "button",
            icon: "fa-light fa-close",
            text: "Close",
            cls: 'bg-[transparent] text-[#3884e1] border-[1.5px] !rounded-md dark:border-[#373638]',
            onClick: "up.close"
          }
        }
      }
    };
  }

  editRecord(record, context, otherParams) {
    super.editRecord(record, context, otherParams)
    const { linkedTaskField } = this.widgetMap;
    this.record = record;
    this.context = context;
    this.otherParams = otherParams;
    this.swivuStore = this.context.project.taskBudgetStore;
    this.annotateBudgetFields();
    this.applyNegativeFieldsStyle();
    this.loadContextMenu(record, context);
    this.displayRecordMetaInformation();

    if (record.archived) this.swivuSetTitleLineThrough();

    if (this.record.isPhantom) {
      this.updateFieldsState(true);

      /* Add listener to check If record is persisted then enable fields */
      const detacher = this.swivuStore.on({
        idChange({ record }) {
          if (record.id === this.record.id) {
            // Bug: When phantom record is persisted, Fields are not updated automatically
            // Therefore reassigning record to update UI
            this.record = record;

            this.updateFieldsState(false);
            this.displayRecordMetaInformation();
            this.element.classList.add("swivu-disabled-field-style");
            detacher();
          }
        },
        thisObj: this,
      })

    } else {
      this.updateHeaderTitle();
      this.element.classList.add("swivu-disabled-field-style");
    }

    /* Attaching change listener to annotate fields */
    if(!this.swivuChangeListener) {
      this.swivuChangeListener = this.swivuStore.on({
        update({ record }) {
          if (record.id === this.record.id) {
            this.annotateBudgetFields();
            this.applyNegativeFieldsStyle();
          }
        },
        thisObj: this
      })
    }

    this.changeCrossToEsc();
    linkedTaskField.store.data = context.project.extraTaskStore.query(() => true, true);
    linkedTaskField.value = record.budgetTasks;
  }

  onEditorHide() {
    if(this.swivuChangeListener) {
      this.swivuChangeListener(); // Detaching listener for change
      this.swivuChangeListener = null;
    }
  }

  async onRecordChange({ source, value, userAction }) {
    if (source.ref === "budgetIdField") {
      if(value.trim() === "" || value === this.record[TASK_BUDGET_FIELDS.title]) return;
      const isDuplicated = await this.checkBudgetDuplication(value);
      if (isDuplicated) {
        source.setError("Another budget record with the same account number exists");
        return;
      } else {
        source.clearError();
      }
    }

    if (userAction) {
      this.record[source.name] = value ?? 0;
      if (this.record.isPhantom) {
        this.swivuStore.add(this.record);
      }
    }

    if (source.ref === "budgetIdField" || source.ref === "descriptionField") {
      this.updateHeaderTitle();
    }

    if (this.otherParams?.onSwivuTabOnRecordChangeHandler) {
      this.otherParams.onSwivuTabOnRecordChangeHandler({swivuTab: this, record: this.record, value, source, userAction});
    }
  }

  onLinkedTaskChange({ source, value: newTasks, oldValue: previousTasks, userAction }) {
    if (!userAction) return;
    if (showArchiveRecordAlert(this.record) || showArchiveRecordAlert(newTasks)) {
      // Bug: Triggering Maximum Call Stack
      source.suspendEvents();
      source.value = previousTasks ?? [];
      source.resumeEvents();
    } else {
      this.record.beginBatch();
      if (previousTasks && previousTasks.length > 0) {
        previousTasks.forEach((taskRecord) => taskRecord.budgetId = null);
      }
      newTasks.forEach((taskRecord) => taskRecord.budgetId = this.record.id);
      this.record.endBatch();
    }
  }

  async checkBudgetDuplication(value) {
    try {
      const response = await checkIfBudgetIdExists(this.context.boardManager.selectedBoard.id, value.trim());
      return response.data;
    } catch (error) {
      showToast({msg: "Something went wrong"})
      console.error('error', error);
    }
  }

  async onBudgetIdFieldInput({ source }) {
    if(!source.isValid) {
      source.clearError();
    }
  }

  annotateBudgetFields() {
    const { headerContainer, bodyContainer, footerContainer } = this.widgetMap;
    [headerContainer, bodyContainer, footerContainer].forEach((container) => (
      container.eachWidget((widget) => {
        if (widget.isField) annotateField(widget.element, widget.name, this.record)
      })
    ))
  }

  // Todo: Unify negative value logic for column renderer and fields
  applyNegativeFieldsStyle() {
    const { laborVarianceField, materialVarianceField, subsVarianceField, rentalVarianceField, miscellaneousVarianceField, daysVarianceField, totalCostVarianceField, grossProfitVarianceField, grossMarginVarianceField } = this.widgetMap;
    [laborVarianceField, materialVarianceField, subsVarianceField, rentalVarianceField, miscellaneousVarianceField, daysVarianceField, totalCostVarianceField, grossProfitVarianceField, grossMarginVarianceField].forEach(field => {
      field.element.classList.remove("!text-[#00b900]", "!text-[#ff0000]")
      if (this.record[field.name] < 0) { /* If value is negative */
        field.element.classList.add("!text-[#ff0000]") // add red color
      } else if(this.record[field.name] > 0) { /* If value is positive */
        field.element.classList.add("!text-[#00b900]") // add green color
      }
    })
  }

  updateFieldsState (isDisabled = true) {
    const { linkedTaskField, descriptionField, contractAmountField, changeOrdersField, laborEstimatedField, laborActualField, materialEstimatedField, materialActualField, subsEstimatedField, subsActualField, rentalEstimatedField, rentalActualField,miscellaneousEstimatedField, miscellaneousActualField, daysEstimatedField } = this.widgetMap;

    [linkedTaskField, descriptionField, contractAmountField, changeOrdersField, laborEstimatedField, laborActualField, materialEstimatedField, materialActualField, subsEstimatedField, subsActualField, rentalEstimatedField, rentalActualField,miscellaneousEstimatedField, miscellaneousActualField, daysEstimatedField].forEach(field => field.disabled = isDisabled);
  }

  updateHeaderTitle() {
    this.title = this.record[TASK_BUDGET_FIELDS.budgetTitleDisplayValue] || this.record[TASK_BUDGET_FIELDS.title];
  }

  loadContextMenu(record, context) {
    this.configureEditorContextMenu(record, context, {
      recordStore: context.project.taskBudgetStore,
      items: {
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.archiveItem]: { enabled: true },
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.deleteItem]: { enabled: true },
      }
    });
  }

  displayRecordMetaInformation() {
      const bbar = this.widgetMap.bbar;
      if (bbar && bbar.getWidgetByRef("dateMetaDataField")) {
        const dateMetaDataField = bbar?.widgetMap?.dateMetaDataField;
        const { createdAt, updatedAt, updaterName } = this.record;

        if ((this.record.isPhantom || !dateMetaDataField || (!createdAt && !updatedAt))) {
          dateMetaDataField.hidden = true;
        }

        if (dateMetaDataField && !this.record.isPhantom) {
          const dateFormat = "M/D/YYYY";
          const commonCls = "text-[#6c6b6b] overflow-hidden overflow-ellipsis";
          dateMetaDataField.html = `
          <div class="overflow-hidden font-normal flex flex-col gap-[0.2em]">
            <span class="${commonCls}">Created: ${defaultCellRenderer({value: DateHelper.format(new Date(createdAt), dateFormat)})}</span>
            <span class="${commonCls}">Last Updated By: ${defaultCellRenderer({value: updaterName})}</span>
            <span class="${commonCls}">Last Updated: ${defaultCellRenderer({value: DateHelper.format(new Date(createdAt), dateFormat)})}</span>
          </div>
          `;
          dateMetaDataField.hidden = false;
        }
      }
    }
}

SwivuBudgetEditor.initClass();

const SwivuBudgetPopup = ({
  context,
  record,
  onClose,
  isShow,
  editorInstance
}) => {
  const { budget, onSwivuTabOnRecordChangeHandler } = record || {};
  const onShowHandler = useCallback(
    (editor, otherParams = {}) => {
      editor.project = context.project;
      editor.editRecord(
        budget,
        context,
        Object.assign(otherParams, { onSwivuTabOnRecordChangeHandler })
      );
      editorInstance.editorRef = editor;
    },
    [budget, context, editorInstance]
  );
  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuBudgetEditor}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  );
};

export default SwivuBudgetPopup;
